import { injectable } from "inversify";
import { Endpoints } from "../shared/enum";

export interface UserData {
    id: string;
    username: string;
    email: string;
    name: string;
    is_active: boolean;
    created_at: string;
    last_login: string;
    is_oauth_user: boolean;
}

export interface AuthService {
    login(username: string, password: string): Promise<UserData>;
    register(username: string, email: string, password: string): Promise<UserData>;
    logout(): Promise<void>;
    getCurrentUser(): Promise<UserData | null>;
    isAuthenticated(): Promise<boolean>;
}

@injectable()
export class DefaultAuthService implements AuthService {
    private readonly baseUrl = Endpoints.BASE_URL;

    /**
     * Login user with username and password.
     * The backend sets an HTTPOnly cookie which will be automatically included in future requests.
     */
    async login(username: string, password: string): Promise<UserData> {
        const response = await fetch(`${this.baseUrl}/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                username,
                password,
            }),
            credentials: "include", // Important: Include cookies in requests
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: "Login failed" }));
            throw new Error(error.detail || "Login failed");
        }

        return await response.json();
    }

    /**
     * Register a new user.
     * The backend automatically logs in the user after registration.
     */
    async register(username: string, email: string, password: string): Promise<UserData> {
        const response = await fetch(`${this.baseUrl}/auth/register`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                username,
                email,
                password,
            }),
            credentials: "include",
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: "Registration failed" }));
            throw new Error(error.detail || "Registration failed");
        }

        return await response.json();
    }

    /**
     * Logout current user by clearing the auth cookie.
     */
    async logout(): Promise<void> {
        await fetch(`${this.baseUrl}/logout`, {
            method: "POST",
            credentials: "include",
        });
    }

    /**
     * Get current authenticated user information.
     */
    async getCurrentUser(): Promise<UserData | null> {
        try {
            const response = await fetch(`${this.baseUrl}/me`, {
                method: "GET",
                credentials: "include",
            });

            if (!response.ok) {
                return null;
            }

            return await response.json();
        } catch (error) {
            console.error("Failed to get current user:", error);
            return null;
        }
    }

    /**
     * Check if user is currently authenticated.
     */
    async isAuthenticated(): Promise<boolean> {
        const user = await this.getCurrentUser();
        return user !== null;
    }
}
